import { Button } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SubscriptionPlanConfig, SubscriptionResponse } from '@/api';
import { EmptyText } from '@/components/Ui/EmptyText';
import { Section, SectionBody } from '@/components/Ui/Section';
import { formatEur, vfiatToEur } from '@/lib/money';
import { shortDate } from '@/lib/relative-time';
import shared from '../AccountScreens.module.css';
import classes from './Billing.module.css';
import { allowanceSummary, planLabel } from './billing-labels';

export interface PlansSectionProps {
  plans: SubscriptionPlanConfig[] | null;
  subscription: SubscriptionResponse | null;
  currentPlanId: string | null;
  onSwitch: (plan: SubscriptionPlanConfig) => Promise<void>;
}

export function PlansSection({ plans, subscription, currentPlanId, onSwitch }: PlansSectionProps) {
  const { t, i18n } = useTranslation();
  const [busy, setBusy] = useState<string | null>(null);

  const nextId =
    subscription?.next_plan_id && subscription.next_plan_id !== subscription.plan_id
      ? subscription.next_plan_id
      : null;

  const sorted = [...(plans ?? [])].sort((a, b) => a.price - b.price);

  return (
    <Section id="plans" title={t('billing.plans.title')} subtitle={t('billing.plans.subtitle')}>
      {plans === null ? (
        <SectionBody>
          <EmptyText>{t('ui.loading')}</EmptyText>
        </SectionBody>
      ) : sorted.length === 0 ? (
        <SectionBody>
          <EmptyText>{t('billing.plans.none')}</EmptyText>
        </SectionBody>
      ) : (
        <div className={classes.plans}>
          {sorted.map((plan) => {
            const current = plan.id === currentPlanId;
            const upcoming = plan.id === nextId;
            const name = planLabel(plan.name);
            return (
              <div
                key={plan.id}
                className={`${classes.plan} ${current ? classes.planCurrent : ''}`}
              >
                <span className={classes.planName}>{name}</span>
                <span className={classes.planPrice}>
                  {formatEur(vfiatToEur(plan.price), i18n.language, 0, 2)}
                  {plan.price > 0 && (
                    <span className={shared.help} style={{ fontWeight: 400 }}>
                      {' '}
                      {t('billing.plans.perMonth')}
                    </span>
                  )}
                </span>
                <span className={shared.sub}>
                  {allowanceSummary(plan.allowances ?? [], t, i18n.language)}
                </span>
                {plan.max_retention_days ? (
                  <span className={shared.sub}>
                    {t('billing.plans.retention', { days: plan.max_retention_days })}
                  </span>
                ) : null}
                <Button
                  size="xs"
                  variant="default"
                  className={classes.planButton}
                  disabled={current || upcoming}
                  loading={busy === plan.id}
                  onClick={async () => {
                    setBusy(plan.id);
                    try {
                      await onSwitch(plan);
                    } finally {
                      setBusy(null);
                    }
                  }}
                >
                  {current
                    ? t('billing.plans.yours')
                    : upcoming && subscription
                      ? t('billing.plans.starts', {
                          date: shortDate(subscription.current_period_end, i18n.language),
                        })
                      : t('billing.plans.switchTo', { name })}
                </Button>
              </div>
            );
          })}
        </div>
      )}
      <div className={classes.note}>{t('billing.plans.retentionNote')}</div>
    </Section>
  );
}
